import { useMfeOptions } from 'src/contexts/MfeOptionsContext';
import { useShellRootContext } from 'src/contexts/ShellRoot';
import { Logger } from 'src/utils/Logger';
import { OnboardingStep } from 'src/types/onboardingStep';
import { TenantSelectionStrategy } from 'src/types/mfeProps';
import { useFinishExecution } from './useFinishExecution';
import { TenantVisualizationType } from '@jarvis/shell-commons/dist/interface/v1/TenantHandler/types';
import { publishEvent } from 'src/analytics/publishEvent';
import { BackFromHpIdType, ScreenAction } from 'src/analytics/types';
import useIsOAuthToken from 'src/utils/IsOAuthToken';
import { AUTH_MODE } from 'src/constants/SimpleUIEvents';
import { useJweb } from './useJweb';
import {
  ValueStoreGetResult,
  ValueStoreGetSuccessResult
} from '@jarvis/shell-commons/dist/services/JWeb/types';
import useFlags from 'src/utils/useFlagWrapper';
import { FLAG_KEYS } from 'src/constants/launchDarklyFlagKeys';
import {
  useMultiUiBrandContext,
  UiTheme
} from '@jarvis/react-setup-and-onboarding-addons';
import { useErrorContext } from 'src/contexts/ErrorContext';
import { ErrorCodes } from 'src/types/error';
import { getUrlSearchParams } from 'src/utils/routing';
import { cancelAuthRequest } from 'src/utils/authCancel';
import { ApplicationContext } from 'src/types/shell';
import { useOnboardingContext } from 'src/contexts/OnboardingContext';

/**
 * Module-level flag used by handleHpxTryAgain to suppress the "userDeclined" error
 * that cancelAuthRequest triggers on the pending signIn() call.
 * Lives outside the hook so it is shared across all useLogin() instances
 * (e.g. UserOnboarding starts the auth, LoadingScreen handles the retry).
 */
let ignoreNextDeclined = false;

export const useLogin = () => {
  const {
    interfaces: { v1 }
  } = useShellRootContext();
  const {
    options: {
      accountLandingPage,
      tenantSelectionStrategy,
      tenantSelectionStrategyRules,
      propositionScreen,
      forceLogin
    }
  } = useMfeOptions();
  const { finishOnSuccess } = useFinishExecution();
  const { handleAuthError, updateError } = useErrorContext();
  const { Plugins } = useJweb();
  const featureFlags = useFlags();
  const { uiBrand } = useMultiUiBrandContext();
  const { onboardingStep, setOnboardingStep } = useOnboardingContext();

  const isOAuthToken = useIsOAuthToken();

  /**
   * Executes an authentication method and handles the response.
   * Returns true if authentication was successful, false otherwise.
   *
   * @param authMethod - The async authentication function to call
   * @param authMethodName - Name for logging purposes
   * @param errorFallbackMessage - Default error message if none provided
   */
  const executeAuthMethod = async (
    authMethod: () => Promise<{
      success?: boolean;
      error?: { message?: string };
    }>,
    authMethodName: string,
    errorFallbackMessage: string
  ): Promise<boolean> => {
    let errorMessage: string | null = null;

    try {
      Logger.log(`Calling ${authMethodName} for authentication`);
      const authenticationResponse = await authMethod();
      Logger.debug('Authentication response:', authenticationResponse);

      if (authenticationResponse?.success) {
        await handleLogin();
        return true;
      }

      errorMessage = authenticationResponse?.error?.message;
    } catch (error) {
      Logger.error(`Error occurred during ${authMethodName}:`, error);
      errorMessage = (error as Error).message;
    }

    const errorToHandle = errorMessage || errorFallbackMessage;

    if (ignoreNextDeclined) {
      ignoreNextDeclined = false;
      Logger.log(
        'Ignoring auth error response as a try again was requested.',
        errorToHandle
      );
      return false;
    }

    Logger.log(`${authMethodName} failed, handling error:`, errorToHandle);
    handleAuthError(errorToHandle);
    return false;
  };

  /**
   * Handles the login process.
   *
   * Checks if the user is already logged in, and performs appropriate actions based on the user's login status.
   */
  const handleLogin = async () => {
    Logger.log('Login started');

    // NEW: Check for force login requirement
    const { didForceLogin } = getUrlSearchParams({ navigation: v1.navigation });

    if (forceLogin && !didForceLogin) {
      Logger.log('Force login requested');
      return await executeForceLogin();
    }

    const isLoggedIn = v1.sessionInterface.isLoggedIn();
    Logger.log(`User is logged in: ${isLoggedIn}`);

    if (isLoggedIn) {
      await handleLoginRedirect();
      await verifyIsUserOnboarded();
      return;
    }

    if (onboardingStep === null) return verifyHasToShowValueProposition();

    login();
  };

  /**
   * Redirects the user to the login page with optional parameters.
   *
   * @param {Object} [loginParams] - Optional parameters for the login process.
   * @param {boolean} [loginParams.accountLandingPage] - Indicates if the account landing page should be shown.
   */
  const login = async (loginParams?: { accountLandingPage: boolean }) => {
    Logger.log('User is not logged in. Redirecting to login page.');
    Logger.log('accountLandingPage', accountLandingPage);

    // adding a new step so the renderOnboardingStep() doesn't recall the LoadingScreen while redirecting to login
    setOnboardingStep(OnboardingStep.LOGIN);
    // Analytics - send event Simple UI
    publishEvent({ type: ScreenAction.REDIRECT_TO_HP_ID });
    localStorage.setItem('redirectedFromLogin', 'loginRedirect');

    const shouldShowAccountLandingPage =
      loginParams?.accountLandingPage ?? accountLandingPage === 'signup';

    // Load Launch Darkly flag to determine if we should should use signIn() on OD flow inside HPX
    const shouldUseSignInOnODFlowInsideHpx = !!(
      featureFlags as Record<string, boolean>
    )[FLAG_KEYS.SIGN_IN_ON_OD_FLOW_INSIDE_HPX];

    // If the SIGN_IN_ON_OD_FLOW_INSIDE_HPX flag is enabled, on OD flows inside HPX (i.e. uiBrand is HPX and application context is NATIVE_PORTAL,
    //  not NATIVE_APP), we should use signIn(). If disabled, will use the default behavior of redirecting to the login page.
    const isNativeApp =
      (v1 as any).app?.getApplicationContext?.() === 'NATIVE_APP';
    const isHpx = shouldUseSignInOnODFlowInsideHpx
      ? uiBrand === UiTheme.hpx || isNativeApp
      : isNativeApp;

    if (isHpx) {
      Logger.log('Redirecting to HPX login flow');
      Logger.log(
        'Will call the saveShowAccountLandingPage with shouldShowAccountLandingPage:',
        shouldShowAccountLandingPage
      );
      await saveShowAccountLandingPage(shouldShowAccountLandingPage);
      Logger.log(
        'Did saveShowAccountLandingPage complete, proceeding to signIn() call.'
      );

      await executeAuthMethod(
        () => (v1 as any).sessionInterface.signIn(),
        'signIn()',
        'Authentication failed'
      );
      return;
    }

    const loginURL = new URL(
      window.location.href + v1.userSession.getLoginPath()
    );
    loginURL.searchParams.append('skipAuthErrorScreen', 'true');
    // This will prevent the onboarding to happening on ClientOS' side.
    // By setting this, the onboarding login and userOnboardingPath will be ignored during the authentication flow,
    // preventing the previous unnecessary redirects from JShellServiceRouting to Navigation and back to JShellServiceRouting, which caused a bad user experience with loading screens flashes.
    // Now, after login, the user will be redirected back to the JShellServiceRouting flow directly and the onboarding will continue from there.
    loginURL.searchParams.append('skipUserOnboarding', 'true');

    if (shouldShowAccountLandingPage)
      loginURL.searchParams.append('target', 'create');

    Logger.log(`Redirecting to ${'/login' + loginURL.search}`);
    (v1.navigation as any).redirect('/login' + loginURL.search);
  };

  /**
   * Executes force login flow by adding didForceLogin parameter and triggering authentication
   * based on application context (native vs portal).
   */
  const executeForceLogin = async () => {
    Logger.log('Executing force login flow');

    // Determine application context first
    const appContext = (v1 as any).app?.getApplicationContext?.();
    Logger.log(`Application context: ${appContext}`);

    // Add didForceLogin parameter to URL
    try {
      const urlSearchParams = new URLSearchParams(window.location.search);
      urlSearchParams.set('didForceLogin', 'true');

      (v1.navigation as any).replace({
        ...v1.navigation.location,
        search: urlSearchParams.toString()
      });
    } catch (error) {
      Logger.error('Failed to update URL with didForceLogin parameter:', error);
      // Proceed with force login anyway (non-terminal error)
    }

    // Handle NATIVE_PORTAL context: use authProvider.forceLogin()
    if (
      appContext === ApplicationContext.NATIVE_PORTAL ||
      appContext === ApplicationContext.NATIVE_APP
    ) {
      Logger.log('Triggering native force login');
      await executeAuthMethod(
        () => (v1 as any).authProvider.forceLogin(),
        'forceLogin()',
        'Force login authentication failed'
      );
      return;
    } else {
      // Portal context (WEB_PORTAL or unknown): redirect to /login
      Logger.log('Redirecting to login page for force login');

      // Set onboarding step before redirect (prevents loading screen loop)
      setOnboardingStep(OnboardingStep.LOGIN);

      // Use existing login() function which handles the redirect
      try {
        return await login();
      } catch (error) {
        Logger.error('Force login redirect failed:', error);
        updateError({
          errorCode: ErrorCodes.ForceLoginError,
          message: 'Unable to complete forced authentication. Please try again.'
        });
        return;
      }
    }
  };

  const verifyIsUserOnboarded = async () => {
    Logger.log('User is already logged in. Verifying if user is onboarded...');

    try {
      const isUserOnboarded =
        await v1.userInterface.onboarding.isUserOnboarded();
      Logger.log(`User is onboarded: ${isUserOnboarded}`);
      if (!isUserOnboarded) {
        Logger.log('User is not onboarded. Start Onboarding');
        setOnboardingStep(OnboardingStep.ONBOARD_USER);
        return;
      }
    } catch (error) {
      // Enhanced error logging with structured context, stack traces, and detailed information
      Logger.error(
        'Failed to verify user onboarding status in verifyIsUserOnboarded:',
        {
          error,
          context: {
            function: 'verifyIsUserOnboarded',
            operation: 'Shell.v1.userInterface.onboarding.isUserOnboarded',
            timestamp: new Date().toISOString()
          }
        }
      );

      // Update error context with user-friendly message
      updateError({
        errorCode: ErrorCodes.IsUserOnboardedError,
        message: 'Unable to verify user onboarding status. Please try again.'
      });

      return;
    }

    const tenantList = await v1.tenantHandlerInterface.getTenantList({
      authContext: 'tenant' as any
    });
    Logger.log('Tenant list:', tenantList);

    if (!tenantList.length) {
      Logger.log('User is not onboarded (Tenant List Empty). Start Onboarding');
      setOnboardingStep(OnboardingStep.ONBOARD_USER);
      return;
    }

    if (tenantList.length === 1) return pickOnlyTenant(tenantList);

    switch (tenantSelectionStrategy) {
      case TenantSelectionStrategy.ShowTenantSelector:
        return showTenantSelector();
      case TenantSelectionStrategy.UseLastTenant:
        return await pickLastTenant();
      case TenantSelectionStrategy.PickFirstUsingRules:
        Logger.warn(
          'DEPRECATION WARNING: PickFirstUsingRules strategy is deprecated and will be removed in a future version. ' +
            'The removal of tenantType from the API will make this feature non-functional. ' +
            'Please use ShowTenantSelector or UseLastTenant strategies instead.'
        );
        return await pickFirstUsingRules(tenantList);
      default:
        Logger.log(
          'User is already onboarded and no TenantSelectionStrategy was set.'
        );
        return setOnboardingStep(OnboardingStep.SELECT_TENANT);
    }
  };

  const verifyHasToShowValueProposition = () => {
    Logger.log(
      'User is not logged in. Verifying if should show Value Proposition...'
    );

    const enableValuePropositionFlag = Boolean(
      (featureFlags as Record<string, boolean>)[
        FLAG_KEYS.ENABLE_VALUE_PROPOSITION
      ]
    );

    const shouldShowValueProposition = Boolean(propositionScreen);

    if (enableValuePropositionFlag && shouldShowValueProposition) {
      Logger.log('Showing Value Proposition screen before login.');
      return setOnboardingStep(OnboardingStep.VALUE_PROPOSITION);
    }

    Logger.log('Proceeding to login without showing Value Proposition.');
    return login();
  };

  const handleLoginRedirect = async () => {
    const redirectedFromLogin = await localStorage.getItem(
      'redirectedFromLogin'
    );

    if (redirectedFromLogin === 'loginRedirect') {
      const result = await isOAuthToken();
      const isLoggedIn = v1?.sessionInterface?.isLoggedIn();
      const isUserOnboarded =
        await v1.userInterface.onboarding.isUserOnboarded();

      publishEvent({
        type: ScreenAction.BACK_FROM_HP_ID,
        payload: {
          screenMode: `${result ? AUTH_MODE.ONE_CLOUD : AUTH_MODE.STRATUS}`,
          actionAuxParams: `status=${isLoggedIn && isUserOnboarded ? BackFromHpIdType.SIGNED_IN_PREVIOUSLY_ONBOARDED : isLoggedIn && !isUserOnboarded ? BackFromHpIdType.SIGNED_IN_NOT_PREVIOUSLY_ONBOARDED : 'Unknown'}`
        }
      });
      localStorage.removeItem('redirectedFromLogin');
    }
  };

  const showTenantSelector = () => {
    Logger.log('Select tenant flow triggered.');
    setOnboardingStep(OnboardingStep.SELECT_TENANT);
  };

  const pickLastTenant = async () => {
    Logger.error(
      'USE_LAST_TENANT strategy not implemented, using SHOW_TENANT_SELECTOR strategy as fallback.'
    );
    showTenantSelector();
  };

  /**
   * @deprecated This function is deprecated and will be removed in a future version.
   * The removal of tenantType from the API will make this feature non-functional.
   * Please use ShowTenantSelector or UseLastTenant strategies instead.
   */
  const pickFirstUsingRules = async (tenantList: TenantVisualizationType[]) => {
    Logger.warn(
      'DEPRECATION WARNING: Using deprecated pickFirstUsingRules function. ' +
        'This feature will be removed in a future version due to tenantType API changes.'
    );
    Logger.log('tenantSelectionStrategyRules', tenantSelectionStrategyRules);
    let selectedTenant = tenantList[0];
    Logger.log('Default tenant:', selectedTenant);
    if (tenantSelectionStrategyRules) {
      Logger.log('Rules provided for PICK_FIRST_USING_RULES.');
      for (const rule of tenantSelectionStrategyRules) {
        const matchedTenant = tenantList.find(
          (tenant) => tenant.type === rule.type
        );
        if (matchedTenant) {
          selectedTenant = matchedTenant;
          break;
        }
      }
    }
    Logger.log('Selected tenant:', selectedTenant);
    await v1.tenantHandlerInterface.setTenant({
      tenantId: selectedTenant.id,
      authContext: 'tenant' as any,
      preventReload: true
    });
    finishOnSuccess(selectedTenant.id);
  };

  const pickOnlyTenant = async (tenantList: TenantVisualizationType[]) => {
    const tenant = tenantList[0];
    await v1.tenantHandlerInterface.setTenant({
      tenantId: tenant.id,
      authContext: 'tenant' as any,
      preventReload: true
    });
    finishOnSuccess(tenant.id);
  };

  const saveShowAccountLandingPage = async (
    shouldRedirectToAccountCreation: boolean
  ) => {
    // Load the Value Store key to be used
    const valueStoreKey =
      (window.Shell as any).manifest?.services?.login
        ?.overrideClientOSAdditionalAuthorizationParams ??
      'clientOSAdditionalAuthorizationParams';
    Logger.log(`Saving login preferences on login: ${valueStoreKey}`);
    // Loads the string from key 'clientOSAdditionalAuthorizationParams' on JWeb Value Store
    const options = {
      keys: [valueStoreKey]
    };
    const results = (
      (await Plugins.ValueStore.get(options)) as ValueStoreGetResult
    )?.result as [ValueStoreGetSuccessResult];

    // If the result is not an array or is empty, we don't need to save anything
    if (!Array.isArray(results) || results.length <= 0) return;

    // Parses the string to an object
    let parsedResult = {};
    try {
      parsedResult = JSON.parse(results[0].value) ?? {};
    } catch (error) {
      Logger.warn(
        'Failed to parse ValueStore result, using empty object:',
        error
      );
      parsedResult = {};
    }

    const shouldApplyNewTheming = !!(featureFlags as Record<string, boolean>)[
      FLAG_KEYS.APPLY_NEW_THEMING
    ];

    // Add or update the 'target' property to 'create'
    // This will redirect the user to the account creation page
    parsedResult = {
      ...parsedResult,
      target: shouldRedirectToAccountCreation ? 'create' : undefined,
      ...(shouldApplyNewTheming && {
        theme_id: v1?.theme?.getUserThemeMode?.() === 'light' ? 'hpapp' : 'dark'
      })
    };
    // Converts the object back to a string
    const newValue = JSON.stringify(parsedResult);
    // Creates the options to save the new value
    const optionsToSave = {
      entries: [
        {
          key: valueStoreKey,
          value: newValue
        }
      ]
    };
    // Saves the new value in the JWeb Value Store
    await Plugins.ValueStore.put(optionsToSave);
  };

  /**
   * Handles the "try again" action from the HPX loading screen.
   * Sets a flag to suppress the "userDeclined" error that cancelAuthRequest triggers on the
   * pending signIn() call, then restarts the login flow.
   */
  const retryAuthentication = async () => {
    ignoreNextDeclined = true;
    await cancelAuthRequest();
    await handleLogin();
  };

  const cancelAuthentication = async () => {
    ignoreNextDeclined = false;
    await cancelAuthRequest();
  };

  return {
    handleLogin,
    login,
    onboardingStep,
    retryAuthentication,
    cancelAuthentication
  };
};
